import { SEO } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, FileText, AlertTriangle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "20 décembre 2024";
  const companyName = "EasyFinder";
  const websiteUrl = "https://easyfinder.fr";
  const contactEmail = "contact@easyfinder.fr";
  const dpoEmail = "dpo@easyfinder.fr";

  return (
    <>
      <SEO
        title="Politique de Confidentialité | EasyFinder"
        description="Politique de confidentialité d'EasyFinder. Découvrez comment nous collectons, utilisons et protégeons vos données personnelles."
        canonical="/privacy-policy"
      />
      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
            <LanguageSwitcher frPath="/privacy-policy" enPath="/privacy-policy-en" />
          </div>

          <article className="prose prose-slate dark:prose-invert max-w-none">
            <h1 className="text-3xl font-bold mb-2">Politique de Confidentialité</h1>
            <p className="text-muted-foreground mb-8">
              Dernière mise à jour : {lastUpdated}
            </p>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p>
                {companyName} ("nous", "notre", "nos") s'engage à protéger la vie privée de ses utilisateurs ("vous", "votre", "vos"). 
                Cette Politique de Confidentialité explique comment nous collectons, utilisons, stockons et protégeons vos données personnelles 
                lorsque vous utilisez notre application de gestion documentaire accessible à l'adresse {websiteUrl} (le "Service").
              </p>
              <p>
                En utilisant notre Service, vous acceptez les pratiques décrites dans cette Politique de Confidentialité. 
                Si vous n'acceptez pas ces pratiques, veuillez ne pas utiliser notre Service.
              </p>
            </section>

            {/* Data Controller */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Responsable du Traitement</h2>
              <p>Le responsable du traitement des données personnelles est :</p>
              <ul>
                <li><strong>Société :</strong> {companyName}</li>
                <li><strong>Site web :</strong> {websiteUrl}</li>
                <li><strong>Email de contact :</strong> {contactEmail}</li>
                <li><strong>Délégué à la Protection des Données (DPO) :</strong> {dpoEmail}</li>
              </ul>
            </section>

            {/* Data Collected */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Données Personnelles Collectées</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3">3.1 Données que vous nous fournissez directement</h3>
              <ul>
                <li><strong>Informations de compte :</strong> Adresse email, nom complet, photo de profil (optionnel)</li>
                <li><strong>Documents :</strong> Fichiers que vous téléchargez (PDF, images, documents Office)</li>
                <li><strong>Communications :</strong> Messages que vous nous envoyez via le support</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">3.2 Données collectées automatiquement</h3>
              <ul>
                <li><strong>Données d'utilisation :</strong> Pages visitées, fonctionnalités utilisées, horodatages</li>
                <li><strong>Données techniques :</strong> Type de navigateur, système d'exploitation, adresse IP (anonymisée dans les logs)</li>
                <li><strong>Données de session :</strong> Tokens d'authentification (chiffrés AES-256-GCM)</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">3.3 Données provenant de services tiers</h3>
              
              <h4 className="text-lg font-medium mt-4 mb-2">Google (Gmail et Connexion Google)</h4>
              <p>Si vous choisissez de connecter votre compte Gmail, nous accédons à :</p>
              <ul>
                <li>Votre adresse email Google (pour l'authentification)</li>
                <li>Les pièces jointes de vos emails (uniquement si vous activez l'import Gmail)</li>
                <li>Les métadonnées des emails (expéditeur, date, sujet - pour filtrer les pièces jointes pertinentes)</li>
              </ul>
              
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 my-4">
                <h5 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  🔒 Déclaration Google API Limited Use
                </h5>
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  L'utilisation et le transfert à toute autre application des informations reçues des API Google 
                  respectent la <a 
                    href="https://developers.google.com/terms/api-services-user-data-policy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline font-medium"
                  >
                    Politique de données utilisateur des services API Google
                  </a>, y compris les exigences d'utilisation limitée (Limited Use requirements).
                </p>
                <p className="text-blue-700 dark:text-blue-300 text-sm mt-2">
                  Nous utilisons les données Gmail <strong>uniquement</strong> pour :
                </p>
                <ul className="text-blue-700 dark:text-blue-300 text-sm mt-1 ml-4">
                  <li>Importer les pièces jointes de vos emails dans votre espace EasyFinder</li>
                  <li>Extraire le texte des documents pour permettre la recherche</li>
                </ul>
                <p className="text-blue-700 dark:text-blue-300 text-sm mt-2">
                  Nous ne partageons <strong>jamais</strong> vos données Gmail avec des tiers, ne les utilisons pas à des fins publicitaires, 
                  et ne les conservons pas au-delà de ce qui est nécessaire pour fournir le Service.
                </p>
              </div>

              <h4 className="text-lg font-medium mt-4 mb-2">Dropbox</h4>
              <p>Si vous connectez Dropbox, nous accédons aux fichiers que vous sélectionnez pour importation.</p>

              <h4 className="text-lg font-medium mt-4 mb-2">Stripe (Paiements)</h4>
              <p>
                Pour les paiements, nous utilisons Stripe qui collecte directement vos informations de paiement. 
                Nous ne stockons <strong>jamais</strong> vos numéros de carte bancaire. 
                Nous recevons uniquement de Stripe : identifiant client, statut d'abonnement, historique des paiements.
              </p>
            </section>

            {/* Purpose of Processing */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Finalités du Traitement</h2>
              <p>Nous utilisons vos données personnelles pour :</p>
              
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Finalité</th>
                    <th className="border border-border p-3 text-left">Base légale (RGPD)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Fournir et maintenir le Service</td>
                    <td className="border border-border p-3">Exécution du contrat (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Gérer votre compte utilisateur</td>
                    <td className="border border-border p-3">Exécution du contrat (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Traiter vos paiements</td>
                    <td className="border border-border p-3">Exécution du contrat (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Extraire et indexer le contenu de vos documents</td>
                    <td className="border border-border p-3">Exécution du contrat (Art. 6.1.b)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Vous envoyer des notifications (expiration, partages)</td>
                    <td className="border border-border p-3">Intérêt légitime (Art. 6.1.f)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Améliorer le Service et corriger les bugs</td>
                    <td className="border border-border p-3">Intérêt légitime (Art. 6.1.f)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Respecter nos obligations légales</td>
                    <td className="border border-border p-3">Obligation légale (Art. 6.1.c)</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Data Security */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Sécurité des Données</h2>
              <p>Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles pour protéger vos données :</p>
              <ul>
                <li><strong>Chiffrement en transit :</strong> Toutes les communications utilisent HTTPS/TLS 1.3</li>
                <li><strong>Chiffrement au repos :</strong> Les tokens OAuth sont chiffrés avec AES-256-GCM</li>
                <li><strong>Contrôle d'accès :</strong> Politiques RLS (Row Level Security) sur toutes les tables de base de données</li>
                <li><strong>Authentification sécurisée :</strong> OAuth 2.0 avec PKCE, tokens signés HMAC-SHA256</li>
                <li><strong>Anonymisation des logs :</strong> Les emails et identifiants sont anonymisés dans les logs serveur</li>
                <li><strong>Protection CSRF :</strong> Paramètres state signés pour les flux OAuth</li>
                <li><strong>Headers de sécurité :</strong> HSTS, X-Content-Type-Options, Referrer-Policy</li>
              </ul>
            </section>

            {/* Data Retention */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Conservation des Données</h2>
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Type de données</th>
                    <th className="border border-border p-3 text-left">Durée de conservation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Données de compte</td>
                    <td className="border border-border p-3">Jusqu'à suppression du compte</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Documents uploadés</td>
                    <td className="border border-border p-3">Jusqu'à suppression par l'utilisateur ou du compte</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Tokens OAuth (Gmail, Dropbox)</td>
                    <td className="border border-border p-3">Jusqu'à déconnexion du service ou suppression du compte</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Logs d'audit</td>
                    <td className="border border-border p-3">12 mois</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Données de paiement (via Stripe)</td>
                    <td className="border border-border p-3">Selon la politique de Stripe + obligations légales (10 ans)</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* Data Sharing */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Partage des Données</h2>
              <p>Nous ne vendons <strong>jamais</strong> vos données personnelles. Nous partageons vos données uniquement avec :</p>
              
              <h3 className="text-xl font-medium mt-6 mb-3">7.1 Sous-traitants</h3>
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Sous-traitant</th>
                    <th className="border border-border p-3 text-left">Finalité</th>
                    <th className="border border-border p-3 text-left">Localisation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Supabase</td>
                    <td className="border border-border p-3">Hébergement, base de données, authentification</td>
                    <td className="border border-border p-3">UE (AWS eu-central-1)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Cloudflare</td>
                    <td className="border border-border p-3">CDN, protection DDoS, DNS</td>
                    <td className="border border-border p-3">Global (avec options UE)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Stripe</td>
                    <td className="border border-border p-3">Traitement des paiements</td>
                    <td className="border border-border p-3">UE/US (certifié PCI-DSS)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">OpenAI</td>
                    <td className="border border-border p-3">Classification et extraction de documents (optionnel)</td>
                    <td className="border border-border p-3">US (accord DPA)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Resend</td>
                    <td className="border border-border p-3">Envoi d'emails transactionnels</td>
                    <td className="border border-border p-3">US</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-medium mt-6 mb-3">7.2 Autres cas de partage</h3>
              <ul>
                <li><strong>Avec votre consentement :</strong> Lorsque vous partagez un document via un lien de partage</li>
                <li><strong>Obligations légales :</strong> Si requis par la loi ou une décision de justice</li>
                <li><strong>Protection des droits :</strong> Pour protéger nos droits, notre sécurité ou celle d'autrui</li>
              </ul>
            </section>

            {/* International Transfers */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Transferts Internationaux</h2>
              <p>
                Certains de nos sous-traitants sont situés en dehors de l'Espace Économique Européen (EEE). 
                Dans ces cas, nous nous assurons que des garanties appropriées sont en place :
              </p>
              <ul>
                <li>Clauses contractuelles types (CCT) approuvées par la Commission européenne</li>
                <li>Data Privacy Framework (DPF) UE-US pour les transferts vers les États-Unis</li>
                <li>Certifications et mesures de sécurité supplémentaires</li>
              </ul>
            </section>

            {/* Your Rights */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Vos Droits (RGPD)</h2>
              <p>Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :</p>
              
              <ul className="mt-4">
                <li>
                  <strong>Droit d'accès (Art. 15) :</strong> Obtenir une copie de vos données personnelles
                </li>
                <li>
                  <strong>Droit de rectification (Art. 16) :</strong> Corriger vos données inexactes ou incomplètes
                </li>
                <li>
                  <strong>Droit à l'effacement (Art. 17) :</strong> Demander la suppression de vos données 
                  (disponible via Paramètres → Supprimer mon compte)
                </li>
                <li>
                  <strong>Droit à la limitation (Art. 18) :</strong> Limiter le traitement de vos données dans certains cas
                </li>
                <li>
                  <strong>Droit à la portabilité (Art. 20) :</strong> Recevoir vos données dans un format structuré et lisible 
                  (disponible via Paramètres → Exporter mes données)
                </li>
                <li>
                  <strong>Droit d'opposition (Art. 21) :</strong> Vous opposer au traitement basé sur l'intérêt légitime
                </li>
                <li>
                  <strong>Droit de retirer le consentement :</strong> À tout moment pour les traitements basés sur le consentement
                </li>
              </ul>

              <p className="mt-4">
                Pour exercer ces droits, contactez-nous à : <a href={`mailto:${dpoEmail}`} className="text-primary underline">{dpoEmail}</a>
              </p>
              <p>
                Vous avez également le droit de déposer une plainte auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : 
                <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary underline ml-1">www.cnil.fr</a>
              </p>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Cookies</h2>
              <p>Nous utilisons des cookies strictement nécessaires au fonctionnement du Service :</p>
              
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Cookie</th>
                    <th className="border border-border p-3 text-left">Finalité</th>
                    <th className="border border-border p-3 text-left">Durée</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">sb-*-auth-token</td>
                    <td className="border border-border p-3">Authentification utilisateur (Supabase)</td>
                    <td className="border border-border p-3">Session / 7 jours</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">cookie-consent</td>
                    <td className="border border-border p-3">Mémorisation du choix cookies</td>
                    <td className="border border-border p-3">1 an</td>
                  </tr>
                </tbody>
              </table>

              <p className="mt-4">
                Nous n'utilisons <strong>pas</strong> de cookies de tracking, de publicité ou d'analyse comportementale.
              </p>
            </section>

            {/* Children */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Protection des Mineurs</h2>
              <p>
                Notre Service n'est pas destiné aux personnes de moins de 16 ans. 
                Nous ne collectons pas sciemment de données personnelles concernant des mineurs. 
                Si vous êtes parent ou tuteur et pensez que votre enfant nous a fourni des données personnelles, 
                veuillez nous contacter à {contactEmail}.
              </p>
            </section>

            {/* Changes */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Modifications de cette Politique</h2>
              <p>
                Nous pouvons mettre à jour cette Politique de Confidentialité périodiquement. 
                En cas de modifications substantielles, nous vous en informerons par email ou via une notification dans l'application. 
                La date de "Dernière mise à jour" en haut de cette page indique quand cette politique a été révisée pour la dernière fois.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">13. Nous Contacter</h2>
              <p>Pour toute question concernant cette Politique de Confidentialité ou vos données personnelles :</p>
              <ul>
                <li><strong>Email général :</strong> <a href={`mailto:${contactEmail}`} className="text-primary underline">{contactEmail}</a></li>
                <li><strong>Délégué à la Protection des Données :</strong> <a href={`mailto:${dpoEmail}`} className="text-primary underline">{dpoEmail}</a></li>
                <li><strong>Site web :</strong> <a href={websiteUrl} className="text-primary underline">{websiteUrl}</a></li>
              </ul>
            </section>

            {/* Google API Disclosure - Repeated for emphasis */}
            <section className="mb-8 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
              <h2 className="text-2xl font-semibold mb-4 text-amber-800 dark:text-amber-200">
                📋 Annexe : Utilisation des API Google
              </h2>
              <p className="text-amber-700 dark:text-amber-300">
                Cette section détaille notre conformité avec la politique de données utilisateur des services API Google.
              </p>
              
              <h3 className="text-lg font-medium mt-4 mb-2 text-amber-800 dark:text-amber-200">Scopes Gmail utilisés</h3>
              <ul className="text-amber-700 dark:text-amber-300">
                <li><code>https://www.googleapis.com/auth/gmail.readonly</code> - Lecture des emails et pièces jointes</li>
                <li><code>https://www.googleapis.com/auth/userinfo.email</code> - Accès à votre adresse email</li>
                <li><code>https://www.googleapis.com/auth/userinfo.profile</code> - Accès à vos informations de profil de base</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2 text-amber-800 dark:text-amber-200">Utilisation limitée</h3>
              <p className="text-amber-700 dark:text-amber-300">
                Conformément aux exigences d'utilisation limitée de Google :
              </p>
              <ul className="text-amber-700 dark:text-amber-300">
                <li>✅ Nous utilisons les données Gmail <strong>uniquement</strong> pour fournir et améliorer les fonctionnalités d'import de documents</li>
                <li>✅ Nous ne transférons <strong>pas</strong> les données Gmail à des tiers sauf si nécessaire pour fournir le Service</li>
                <li>✅ Nous n'utilisons <strong>pas</strong> les données Gmail à des fins publicitaires</li>
                <li>✅ Nous permettons la suppression complète des données Gmail via les paramètres du compte</li>
                <li>✅ Les tokens OAuth sont chiffrés et stockés de manière sécurisée</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2 text-amber-800 dark:text-amber-200">Révocation de l'accès</h3>
              <p className="text-amber-700 dark:text-amber-300">
                Vous pouvez révoquer l'accès d'EasyFinder à vos données Gmail à tout moment :
              </p>
              <ol className="text-amber-700 dark:text-amber-300">
                <li>Dans EasyFinder : Paramètres → Sources → Gmail → Déconnecter</li>
                <li>Via Google : <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/permissions</a></li>
              </ol>
            </section>

            {/* Related Security Documents */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">14. Documents de Sécurité Connexes</h2>
              <p>
                Pour une transparence complète sur nos pratiques de sécurité, consultez également les documents suivants :
              </p>
              
              <div className="grid gap-4 mt-6 not-prose">
                <Link 
                  to="/information-security-policy" 
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="p-2 rounded-full bg-primary/10">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Politique de Sécurité de l'Information</h3>
                    <p className="text-sm text-muted-foreground">
                      Password Policy, Disaster Recovery, gouvernance de la sécurité
                    </p>
                  </div>
                </Link>

                <Link 
                  to="/incident-response-policy" 
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="p-2 rounded-full bg-destructive/10">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Politique de Réponse aux Incidents</h3>
                    <p className="text-sm text-muted-foreground">
                      Procédures de détection, classification et réponse aux incidents de sécurité
                    </p>
                  </div>
                </Link>

                <Link 
                  to="/vulnerability-disclosure-policy" 
                  className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="p-2 rounded-full bg-amber-500/10">
                    <FileText className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Politique de Divulgation des Vulnérabilités</h3>
                    <p className="text-sm text-muted-foreground">
                      Programme de signalement responsable des failles de sécurité
                    </p>
                  </div>
                </Link>
              </div>
            </section>

          </article>

          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} {companyName}. Tous droits réservés.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
